import React, { lazy, Suspense } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import styles from '../../styles/global.module.css';
import type { ShapePropertyInputDef } from '@/types/commontypes';
import { ShapePropertyInputType, ShapeType } from '@/types/commontypes';

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const componentMap = {
  [ShapePropertyInputType.LINE_OPTION]: lazy(() => import('./PropertyTypes/LineOptions')),
  [ShapePropertyInputType.LINE_WIDTH_DASH_OPTION]: lazy(() => import('./PropertyTypes/LineWidthDashPicker')),
  [ShapePropertyInputType.EXTEND_OPTION]: lazy(() => import('./PropertyTypes/ExtendOption')),
  [ShapePropertyInputType.SHOW_COLOR_OPTIONS]: lazy(() => import('./PropertyTypes/ShowColorOption')),
  [ShapePropertyInputType.SHOW_COLOR_OPTION]: lazy(() => import('./PropertyTypes/ShowColorOption')),
  [ShapePropertyInputType.SHOW_OPACITY_OPTION]: lazy(() => import('./PropertyTypes/ShowOpacityOption')),
  [ShapePropertyInputType.CHECKBOX]: lazy(() => import('./Widgets/Checkbox')),
  [ShapePropertyInputType.TEXT]: lazy(() => import('./Widgets/Text')),
  [ShapePropertyInputType.FONT_OPTION]: lazy(() => import('./PropertyTypes/FontOption')),
  [ShapePropertyInputType.SEPARATOR]: lazy(() => import('./Widgets/Separator')),
  [ShapePropertyInputType.ARROW_OPTION]: lazy(() => import('./PropertyTypes/ArrowOption')),
  [ShapePropertyInputType.SHOW_VALUE_COLOR_OPTIONS]: lazy(() => import('./PropertyTypes/ShowValueColorOption')),
  [ShapePropertyInputType.FONT_BASIC_OPTION]: lazy(() => import('./PropertyTypes/FontOption')),
  [ShapePropertyInputType.SHOW_LINE_OPTION]: lazy(() => import('./PropertyTypes/showLineOption')),
  [ShapePropertyInputType.COLOR_PICKER]: lazy(() => import('./Widgets/ColorPicker')),
  [ShapePropertyInputType.NUMBER]: lazy(() => import('./PropertyTypes/NumberInput')),
  [ShapePropertyInputType.SELECT]: lazy(() => import('./PropertyTypes/SelectInput')),
} as unknown as {
  [key in ShapePropertyInputType]: React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>>;
};

interface DynamicRendererProps {
  type: ShapeType;
  properties: ShapePropertyInputDef[];
  /** Current shape props (flat object keyed by property name) */
  shapeProps: Record<string, unknown>;
  /** Called when user changes a property value */
  onPropChange: (name: string, value: unknown) => void;
  isFromMiniToolbar?: boolean;
}

const DynamicRenderer: React.FC<DynamicRendererProps> = ({
  type,
  properties,
  shapeProps,
  onPropChange,
  isFromMiniToolbar,
}) => {
  return (
    <Suspense
      fallback={
        <div className="flex justify-center py-4">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      }
    >
      <div
        className={`
        max-h-[500px]
        overflow-y-auto
        overflow-x-hidden
        gap-4
        ${!isFromMiniToolbar ? 'p-2' : ''}
        ${isFromMiniToolbar && type === ShapeType.TEXT ? 'flex' : styles.property_grid}
      `}
      >
        {properties.map(prop => {
          const Component = componentMap[prop.type];
          const currentValue: unknown = shapeProps[prop.name];
          const { name: _name, type: _type, showInMiniToolbar: _mini, ...restProps } = prop;

          return (
            Component && (
              <Component
                key={prop.name}
                value={currentValue}
                onChange={(newValue: unknown) => onPropChange(prop.name, newValue)}
                type={type}
                isFromMiniToolbar={isFromMiniToolbar}
                {...restProps}
              />
            )
          );
        })}
      </div>
    </Suspense>
  );
};

export default DynamicRenderer;
